import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

// #168 波浪粒子场 (Wave Particle Field)
// 粒子系统 + 波浪运动 + 流畅视觉效果

interface WaveParticleFieldProps {
  particleCount?: number;
  waveAmplitude?: number;
  waveFrequency?: number;
  backgroundColor?: string;
  primaryColor?: string;
  secondaryColor?: string;
}

export const WaveParticleField: React.FC<WaveParticleFieldProps> = ({
  particleCount = 200,
  waveAmplitude = 50,
  waveFrequency = 0.05,
  backgroundColor = '#0a0a1a',
  primaryColor = '#4ecdc4',
  secondaryColor = '#ff6b6b',
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  // Spring 动画
  const springOpacity = spring({
    frame,
    fps,
    config: {
      damping: 15,
      stiffness: 80,
    },
  });

  const opacity = interpolate(springOpacity, [0, 1], [0, 1]);

  // 生成粒子
  const particles = Array.from({ length: particleCount }, (_, i) => {
    // 初始位置（网格分布）
    const col = i % 20;
    const row = Math.floor(i / 20);
    const xBase = interpolate(col, [0, 19], [100, width - 100]);
    const yBase = interpolate(row, [0, Math.floor(particleCount / 20) - 1], [150, height - 150]);

    // 波浪位移
    const waveOffset = Math.sin(xBase * waveFrequency + frame * 0.1) * waveAmplitude;
    const secondaryWave = Math.cos(yBase * waveFrequency * 0.5 + frame * 0.08) * (waveAmplitude * 0.5);

    const x = xBase;
    const y = yBase + waveOffset + secondaryWave;

    // 粒子大小（基于位置）
    const size = interpolate(
      Math.sin(xBase * waveFrequency + yBase * waveFrequency),
      [-1, 1],
      [3, 8]
    );

    // 粒子颜色（基于距离中心的位置）
    const centerX = width / 2;
    const centerY = height / 2;
    const distanceFromCenter = Math.sqrt(
      Math.pow(xBase - centerX, 2) + Math.pow(yBase - centerY, 2)
    );
    const maxDistance = Math.sqrt(Math.pow(centerX, 2) + Math.pow(centerY, 2));
    const colorRatio = distanceFromCenter / maxDistance;

    // 粒子透明度（基于波浪高度）
    const particleOpacity = interpolate(
      waveOffset,
      [-waveAmplitude, waveAmplitude],
      [0.3, 1]
    );

    return {
      x,
      y,
      size,
      colorRatio,
      opacity: particleOpacity,
    };
  });

  return (
    <AbsoluteFill style={{ backgroundColor }}>
      {/* SVG 粒子场 */}
      <svg
        width={width}
        height={height}
        style={{ opacity, transition: 'opacity 0.3s' }}
      >
        {/* 发光滤镜 */}
        <defs>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* 粒子 */}
        {particles.map((particle, i) => {
          const color = interpolate(
            particle.colorRatio,
            [0, 1],
            [0, 1]
          );
          const r = Math.round(interpolate(color, [0, 1], [78, 255])); // 4ecdc4 -> ff6b6b
          const g = Math.round(interpolate(color, [0, 1], [205, 107]));
          const b = Math.round(interpolate(color, [0, 1], [196, 107]));

          return (
            <circle
              key={i}
              cx={particle.x}
              cy={particle.y}
              r={particle.size}
              fill={`rgba(${r}, ${g}, ${b}, ${particle.opacity})`}
              filter="url(#glow)"
            />
          );
        })}

        {/* 连线（距离近的粒子） */}
        {particles.map((p1, i) => {
          if (i % 5 !== 0) return null; // 只处理部分粒子以提高性能

          return particles.slice(i + 1, i + 10).map((p2, j) => {
            const distance = Math.sqrt(
              Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2)
            );

            if (distance > 80) return null;

            const lineOpacity = interpolate(distance, [0, 80], [0.3, 0], {
              extrapolateRight: 'clamp',
            });

            return (
              <line
                key={`${i}-${j}`}
                x1={p1.x}
                y1={p1.y}
                x2={p2.x}
                y2={p2.y}
                stroke={`rgba(78, 205, 196, ${lineOpacity})`}
                strokeWidth="1"
              />
            );
          });
        })}
      </svg>

      {/* 中心文字 */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: 80,
            fontWeight: 'bold',
            color: '#ffffff',
            textTransform: 'uppercase',
            letterSpacing: '8px',
            textShadow: `
              0 0 20px rgba(78, 205, 196, 0.8),
              0 0 40px rgba(78, 205, 196, 0.6)
            `,
            opacity: interpolate(springOpacity, [0, 1], [0, 1]),
          }}
        >
          WAVE
        </div>
        <div
          style={{
            fontSize: 40,
            color: '#ffffff',
            marginTop: 20,
            textShadow: '0 0 10px rgba(255, 107, 107, 0.6)',
            opacity: interpolate(
              spring({
                frame: frame - 15,
                fps,
                config: { damping: 12, stiffness: 100 },
              }),
              [0, 1],
              [0, 0.8]
            ),
          }}
        >
          PARTICLE FIELD
        </div>
      </div>

      {/* 参数显示 */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          right: 40,
          color: '#ffffff',
          opacity: 0.6,
          fontSize: 14,
          fontFamily: 'monospace',
        }}
      >
        <div>Particles: {particleCount}</div>
        <div>Wave Amplitude: {waveAmplitude}px</div>
        <div>Frame: {frame}</div>
      </div>
    </AbsoluteFill>
  );
};
